import { applyMiddleware, compose, createStore } from 'redux';
import { thunk } from 'redux-thunk';
import { persistStore } from 'redux-persist';
import { createBrowserHistory, createHashHistory, createMemoryHistory } from 'history';
import { createReduxHistoryContext } from 'redux-first-history';
import rootReducer from '@/redux/reducers/rootReducer.js';

const isDevelopment = process.env.NODE_ENV === 'development';
const DEBUG_MODE = false;
const isPublicEmbedDocument = typeof window !== 'undefined' && /^\/embed\/analytics\/[^/]+\/?$/.test(window.location.pathname);

const createRouterHistory = () => {
  if (isPublicEmbedDocument) {
    /* The bearer stays in the browser fragment for reload, never in Redux router state. */
    return createMemoryHistory({ initialEntries: [window.location.pathname + window.location.search] });
  }
  if (typeof window !== 'undefined' && window.location?.protocol === 'file:') {
    return createHashHistory();
  }

  return createBrowserHistory();
};

/* The browser history underneath the redux one. Router actions and navigate() both end in its
   push and replace, which is what makes it the one place a navigation guard can stand. */
const previousRuntime = isDevelopment ? import.meta.hot?.data?.runtime : undefined;
const routerBaseHistory = previousRuntime?.routerBaseHistory || createRouterHistory();

const routerContext = previousRuntime?.routerContext || createReduxHistoryContext({ history: routerBaseHistory });
const { createReduxHistory, routerMiddleware, routerReducer } = routerContext;

const composeEnhancers = isDevelopment && typeof window === 'object' && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__(DEBUG_MODE ? { trace: true, traceLimit: 25 } : { trace: false }) : compose;

const configureStore = (preloadedState) => {
  const middlewares = [routerMiddleware, thunk];

  return createStore(rootReducer(routerReducer), preloadedState, composeEnhancers(applyMiddleware(...middlewares)));
};

const store = previousRuntime?.store || configureStore();
/* Public embeds use this same request store without session hydration or persistence. */
const persistor = previousRuntime ? previousRuntime.persistor : isPublicEmbedDocument ? null : persistStore(store);
const history = previousRuntime?.history || createReduxHistory(store);

if (isDevelopment && import.meta.hot) {
  // Keep requests and mounted Providers on the same store across Vite updates. Recreating
  // history or persistence here would also duplicate their subscriptions.
  if (previousRuntime) {
    store.replaceReducer(rootReducer(routerReducer));
  }
  import.meta.hot.dispose((data) => {
    data.runtime = { store, persistor, history, routerBaseHistory, routerContext };
  });
  import.meta.hot.accept();
}

export { store, persistor, history, routerBaseHistory };
